/**
 * A context to manage the visibility of modal dialogs.
 * It provides a way to increment and decrement the counter of visible modals.
 * This is useful to detect if there are any modals open in the application.
 */

// Libraries
import { useCallback } from "react";
import { createContext, useState, useContext, PropsWithChildren } from "react";

interface TModalDialogCounterContext {
  increment: VoidFunction;
  decrement: VoidFunction;
  isModalVisible: boolean;
}

const ModalDialogCounterContext = createContext<TModalDialogCounterContext>({
  increment: () => {},
  decrement: () => {},
  isModalVisible: false,
});

const ModalDialogCounterProvider = ({ children }: PropsWithChildren) => {
  const [counter, setCounter] = useState(0);

  const increment = useCallback(() => setCounter((prevCounter) => prevCounter + 1), []);
  const decrement = useCallback(() => setCounter((prevCounter) => prevCounter - 1), []);

  const isModalVisible = counter > 0;

  return (
    <ModalDialogCounterContext.Provider value={{ isModalVisible, increment, decrement }}>
      {children}
    </ModalDialogCounterContext.Provider>
  );
};

const useModalDialogCounter = () => useContext(ModalDialogCounterContext);

export { ModalDialogCounterProvider, useModalDialogCounter };
